<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { isCoarsePointerDevice } from '@/platform/viewport/layoutViewport'

/**
 * 工具内统一下拉（自定义列表，闭合/展开样式一致，各端不依赖系统原生选项层）。
 * `useCellStyle=true` 时触发器使用工具统一表单字号/边距；`useCellStyle=false` 时仅用 `inputClass` 做布局修饰。
 */
export type AppSelectOption = {
  label: string
  value: string | number | boolean
  /** 为 true 时该选项不可选（如发布收费方式降级限制） */
  disabled?: boolean
}

const VIRTUAL_THRESHOLD = 50
const ITEM_HEIGHT = 32
const PANEL_MAX_HEIGHT = 240
const PANEL_Z_INDEX = 6000
const POSITION_THROTTLE_MS = 100

const props = withDefaults(defineProps<{
  modelValue: string | number | boolean
  options: AppSelectOption[]
  placeholder?: string
  disabled?: boolean
  size?: 'default' | 'sm'
  wide?: boolean
  inputClass?: string
  useCellStyle?: boolean
}>(), {
  placeholder: '',
  disabled: false,
  size: 'default',
  wide: false,
  inputClass: '',
  useCellStyle: true,
})

const emit = defineEmits<{
  'update:modelValue': [value: string | number | boolean]
}>()

const rootRef = ref<HTMLElement | null>(null)
const triggerRef = ref<HTMLButtonElement | null>(null)
const panelRef = ref<HTMLElement | null>(null)
const listRef = ref<HTMLElement | null>(null)
const isOpen = ref(false)
const listScrollTop = ref(0)
const activeIndex = ref(-1)
const panelStyle = ref<Record<string, string>>({})

let positionTimer = 0
let scrollCaptureAttached = false

/** 收起当前文本输入焦点，避免弹层内误弹系统键盘。 */
function blurActiveTextInput(): void {
  const active = document.activeElement
  if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) {
    active.blur()
  }
}

/** 含占位项在内的完整选项列表。 */
const listOptions = computed(() => {
  const rows: AppSelectOption[] = [...props.options]
  if (props.placeholder) {
    rows.unshift({ label: props.placeholder, value: '' })
  }
  return rows
})

const isVirtualList = computed(() => listOptions.value.length > VIRTUAL_THRESHOLD)

const virtualSlice = computed(() => {
  const total = listOptions.value.length
  if (!isVirtualList.value) {
    return { start: 0, end: total, offsetTop: 0, totalHeight: total * ITEM_HEIGHT }
  }
  const start = Math.max(0, Math.floor(listScrollTop.value / ITEM_HEIGHT) - 2)
  const visibleCount = Math.ceil(PANEL_MAX_HEIGHT / ITEM_HEIGHT) + 4
  const end = Math.min(total, start + visibleCount)
  return {
    start,
    end,
    offsetTop: start * ITEM_HEIGHT,
    totalHeight: total * ITEM_HEIGHT,
  }
})

const visibleOptions = computed(() => {
  const slice = virtualSlice.value
  return listOptions.value.slice(slice.start, slice.end).map((item, index) => ({
    item,
    index: slice.start + index,
  }))
})

const selectedIndex = computed(() =>
  listOptions.value.findIndex((item) => item.value === props.modelValue),
)

const displayLabel = computed(() => {
  const matched = listOptions.value.find((item) => item.value === props.modelValue)
  if (matched) return matched.label
  if (props.placeholder) return props.placeholder
  return String(props.modelValue)
})

const triggerClass = computed(() => [
  'app-select-field__trigger',
  props.inputClass,
  {
    sm: props.useCellStyle && props.size === 'sm',
    wide: props.useCellStyle && props.wide,
    'is-open': isOpen.value,
    'is-placeholder': props.placeholder !== '' && props.modelValue === '',
  },
])

/**
 * 更新浮层位置；空间不足时向上展开。
 */
function updatePanelPosition() {
  const trigger = triggerRef.value
  if (!trigger) return
  const rect = trigger.getBoundingClientRect()
  const viewportH = window.innerHeight
  const panelHeight = Math.min(PANEL_MAX_HEIGHT, listOptions.value.length * ITEM_HEIGHT)
  const openDown = rect.bottom + panelHeight <= viewportH - 8 || rect.top < viewportH / 2
  const top = openDown ? rect.bottom : rect.top - panelHeight
  panelStyle.value = {
    position: 'fixed',
    left: `${Math.max(8, rect.left)}px`,
    top: `${Math.max(8, top)}px`,
    width: `${rect.width}px`,
    zIndex: String(PANEL_Z_INDEX),
  }
}

function schedulePanelPosition() {
  if (positionTimer) return
  positionTimer = window.setTimeout(() => {
    positionTimer = 0
    if (isOpen.value) updatePanelPosition()
  }, POSITION_THROTTLE_MS)
}

function scrollActiveOptionIntoView() {
  const list = listRef.value
  if (!list || activeIndex.value < 0) return
  const top = activeIndex.value * ITEM_HEIGHT
  const bottom = top + ITEM_HEIGHT
  if (top < list.scrollTop) list.scrollTop = top
  else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight
}

function openPanel() {
  if (props.disabled) return
  blurActiveTextInput()
  isOpen.value = true
  activeIndex.value = selectedIndex.value >= 0 ? selectedIndex.value : 0
  listScrollTop.value = 0
  nextTick(() => {
    updatePanelPosition()
    if (isVirtualList.value && listRef.value && activeIndex.value >= 0) {
      const top = activeIndex.value * ITEM_HEIGHT
      listRef.value.scrollTop = top
      listScrollTop.value = top
    }
    scrollActiveOptionIntoView()
    if (!isCoarsePointerDevice()) {
      panelRef.value?.focus()
    }
  })
}

function closePanel() {
  isOpen.value = false
  activeIndex.value = -1
}

function togglePanel() {
  if (isOpen.value) closePanel()
  else openPanel()
}

function selectOption(option: AppSelectOption) {
  if (option.disabled) return
  emit('update:modelValue', option.value)
  closePanel()
  if (isCoarsePointerDevice()) {
    blurActiveTextInput()
    triggerRef.value?.blur()
    return
  }
  triggerRef.value?.focus()
}

function handleTriggerPointerDown(): void {
  blurActiveTextInput()
}

function moveActive(delta: number) {
  const total = listOptions.value.length
  if (total <= 0) return
  let next = activeIndex.value
  if (next < 0) next = 0
  for (let step = 0; step < total; step += 1) {
    next = (next + delta + total) % total
    if (!listOptions.value[next]?.disabled) break
  }
  activeIndex.value = next
  nextTick(scrollActiveOptionIntoView)
}

function handleTriggerKeydown(event: KeyboardEvent) {
  if (props.disabled) return
  if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    if (!isOpen.value) openPanel()
    else if (event.key === 'ArrowDown') moveActive(1)
    return
  }
  if (event.key === 'Escape' && isOpen.value) {
    event.preventDefault()
    closePanel()
  }
}

function handlePanelKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    event.preventDefault()
    closePanel()
    triggerRef.value?.focus()
    return
  }
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    moveActive(1)
    return
  }
  if (event.key === 'ArrowUp') {
    event.preventDefault()
    moveActive(-1)
    return
  }
  if (event.key === 'Enter') {
    event.preventDefault()
    const option = listOptions.value[activeIndex.value]
    if (option) selectOption(option)
  }
}

function handleListScroll(event: Event) {
  listScrollTop.value = (event.target as HTMLElement).scrollTop
}

function handleDocumentPointerDown(event: PointerEvent) {
  if (!isOpen.value) return
  const target = event.target
  if (!(target instanceof Node)) return
  if (rootRef.value?.contains(target) || panelRef.value?.contains(target)) return
  closePanel()
}

function attachScrollCapture() {
  if (scrollCaptureAttached) return
  scrollCaptureAttached = true
  document.addEventListener('scroll', schedulePanelPosition, true)
  window.addEventListener('resize', schedulePanelPosition)
}

function detachScrollCapture() {
  if (!scrollCaptureAttached) return
  scrollCaptureAttached = false
  document.removeEventListener('scroll', schedulePanelPosition, true)
  window.removeEventListener('resize', schedulePanelPosition)
}

watch(isOpen, (open) => {
  if (open) {
    document.addEventListener('pointerdown', handleDocumentPointerDown, true)
    attachScrollCapture()
    return
  }
  document.removeEventListener('pointerdown', handleDocumentPointerDown, true)
  detachScrollCapture()
})

watch(
  () => props.modelValue,
  () => {
    if (!isOpen.value) return
    activeIndex.value = selectedIndex.value >= 0 ? selectedIndex.value : activeIndex.value
  },
)

onBeforeUnmount(() => {
  closePanel()
  document.removeEventListener('pointerdown', handleDocumentPointerDown, true)
  detachScrollCapture()
  if (positionTimer) window.clearTimeout(positionTimer)
})
</script>

<template>
  <div ref="rootRef" class="app-select-field">
    <button
      ref="triggerRef"
      type="button"
      :class="triggerClass"
      :disabled="disabled"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      @pointerdown="handleTriggerPointerDown"
      @click="togglePanel"
      @keydown="handleTriggerKeydown"
    >
      <span class="app-select-field__label">{{ displayLabel }}</span>
      <span class="app-select-field__caret" aria-hidden="true" />
    </button>

    <Teleport to="body">
      <div
        v-show="isOpen"
        ref="panelRef"
        class="app-select-field__panel"
        :style="panelStyle"
        role="listbox"
        tabindex="-1"
        @keydown="handlePanelKeydown"
      >
        <div
          ref="listRef"
          class="app-select-field__list"
          :class="{ 'app-select-field__list--virtual': isVirtualList }"
          @scroll="handleListScroll"
        >
          <div
            v-if="isVirtualList"
            class="app-select-field__virtual-spacer"
            :style="{ height: `${virtualSlice.totalHeight}px` }"
          />
          <div
            class="app-select-field__virtual-window"
            :style="isVirtualList ? { transform: `translateY(${virtualSlice.offsetTop}px)` } : undefined"
          >
            <button
              v-for="row in visibleOptions"
              :key="`app-select-opt-${String(row.item.value)}-${row.index}`"
              type="button"
              class="app-select-field__option"
              :class="{
                'is-selected': row.item.value === modelValue,
                'is-active': row.index === activeIndex,
              }"
              :disabled="Boolean(row.item.disabled)"
              role="option"
              :aria-selected="row.item.value === modelValue"
              @click="selectOption(row.item)"
              @mouseenter="activeIndex = row.index"
            >
              {{ row.item.label }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.app-select-field {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}

.app-select-field__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  width: 100%;
  min-width: 0;
  padding: var(--app-space-xs) var(--app-space-sm);
  font-size: var(--app-font-size-md);
  line-height: var(--app-line-height-normal);
  font-family: inherit;
  color: var(--theme-text-heading);
  background: var(--theme-bg-card);
  border: 1px solid var(--theme-border);
  box-sizing: border-box;
  cursor: pointer;
  text-align: left;
  touch-action: manipulation;
}

.app-select-field__trigger:focus-visible {
  outline: 1px solid var(--theme-focus-outline);
  outline-offset: 0;
  background: var(--theme-bg-input-focus);
}

.app-select-field__trigger.is-open {
  background: var(--theme-bg-input-focus);
}

.app-select-field__trigger:disabled {
  cursor: not-allowed;
  color: var(--theme-text-muted);
}

.app-select-field__trigger.is-placeholder .app-select-field__label {
  color: var(--theme-text-muted);
}

.app-select-field__label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.app-select-field__caret {
  flex-shrink: 0;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid var(--theme-text-muted);
}
</style>

<style>
.app-select-field__panel {
  box-sizing: border-box;
  background: var(--theme-bg-card);
  border: 1px solid var(--theme-border);
}

.app-select-field__list {
  max-height: 240px;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
  scrollbar-color: var(--theme-scrollbar-thumb) var(--theme-scrollbar-track);
}

.app-select-field__list--virtual {
  position: relative;
}

.app-select-field__virtual-spacer {
  width: 1px;
  pointer-events: none;
}

.app-select-field__virtual-window {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
}

.app-select-field__list:not(.app-select-field__list--virtual) .app-select-field__virtual-window {
  position: static;
}

.app-select-field__option {
  display: block;
  width: 100%;
  min-height: 32px;
  padding: 6px 8px;
  border: none;
  background: var(--theme-bg-card);
  color: var(--theme-text-heading);
  font-size: var(--app-font-size-md);
  line-height: var(--app-line-height-normal);
  font-family: inherit;
  text-align: left;
  box-sizing: border-box;
  cursor: pointer;
}

.app-select-field__option:hover,
.app-select-field__option.is-active {
  background: var(--theme-bg-hover);
}

.app-select-field__option.is-selected {
  background: var(--theme-bg-active);
  font-weight: 600;
}

.app-select-field__option:disabled {
  color: var(--theme-text-muted);
  cursor: not-allowed;
  background: var(--theme-bg-card);
}

.app-select-field__option:disabled:hover {
  background: var(--theme-bg-card);
}
</style>
